import { useState, useEffect } from "react";
import { Menu, MenuCategory } from "@/types/menu";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";

type ReorderSectionsDialogProps = {
  menu: Menu;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedMenu: Menu) => void;
  language: string;
};

function SortableItem({
  category,
  language,
}: {
  category: MenuCategory;
  language: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });
  const t = useTranslations("ReorderSectionsDialog");

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 p-3 bg-background border rounded-lg",
        isDragging && "opacity-50"
      )}
    >
      <button
        className="cursor-grab focus:cursor-grabbing p-1 hover:bg-muted rounded"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-5 w-5 text-muted-foreground" />
      </button>
      <div className="flex-1">
        <div className="font-medium">
          {category.name[language] || category.name.japanese}
        </div>
        <div className="text-sm text-muted-foreground">
          {category.items.length}{" "}
          {category.items.length === 1 ? t("item") : t("items")}
        </div>
      </div>
    </div>
  );
}

export default function ReorderSectionsDialog({
  menu,
  isOpen,
  onClose,
  onUpdate,
  language,
}: ReorderSectionsDialogProps) {
  const [categories, setCategories] = useState<MenuCategory[]>(menu.categories);
  const [hasChanged, setHasChanged] = useState(false);
  const { toast } = useToast();
  const t = useTranslations("ReorderSectionsDialog");

  useEffect(() => {
    if (isOpen) {
      setCategories(menu.categories);
      setHasChanged(false);
    }
  }, [isOpen, menu.categories]);

  // Check if order has changed
  const checkOrderChanged = (newCategories: MenuCategory[]) => {
    // If lengths don't match, something changed
    if (newCategories.length !== menu.categories.length) {
      return true;
    }

    // Compare each category by ID in order
    return newCategories.some(
      (category, index) => category.id !== menu.categories[index].id
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCategories((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Check if order has actually changed from the original
        const changed = checkOrderChanged(newItems);
        setHasChanged(changed);

        return newItems;
      });
    }
  };

  const handleSave = () => {
    if (!hasChanged) return;

    const updatedMenu = {
      ...menu,
      categories,
    };
    onUpdate(updatedMenu);
    toast({
      title: t("sectionsReordered"),
      description: t("sectionsReorderedDescription"),
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="p-0 sm:p-6 h-[100dvh] w-[100dvw] sm:h-auto sm:w-auto sm:min-w-[500px] sm:max-w-[600px] sm:max-h-[85vh] flex flex-col space-y-0 overflow-hidden">
        <DialogHeader className="flex-shrink-0 p-6 pb-2 sm:p-0 sm:pb-2">
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>{t("description")}</DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto flex-grow px-6 py-4 sm:px-0">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={categories.map((cat) => cat.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {categories.map((category) => (
                  <SortableItem
                    key={category.id}
                    category={category}
                    language={language}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
        <DialogFooter className="flex-shrink-0 p-6 pt-4 sm:p-0 mt-auto border-t sm:border-0">
          <div className="flex gap-2 w-full sm:justify-end">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 sm:flex-none"
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanged}
              className="flex-1 sm:flex-none"
            >
              {t("saveOrder")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
